import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "../ui/Button";
import Icon from "../ui/Icon";
import IconButton from "../ui/IconButton";
import { UserContext } from "../../contexts";
import { isActiveStoreOwner } from "../../helpers/adminAccess";
import { usePendingChangeRequests } from "../../hooks/useListingChangeRequests";
import NotificationItem from "../../notifications/NotificationItem";
import { groupNotifications } from "../../notifications/notificationPresentation";
import {
  markAllNotificationsRead,
  markNotificationRead,
  useNotificationSummary,
  useRefreshNotifications,
} from "../../notifications/useNotificationCenter";
import "../../notifications/rc-notification-item.scss";
import "./notifications-bell.scss";

/**
 * Header notifications — the corner preview (Fontaine M34, `ft-notif`).
 *
 * Two sources, deliberately kept separate in the panel:
 *  - the general notification-center feed, GROUPED BY KIND (leads, payments,
 *    buying…) with newest first inside each group. Without grouping a busy
 *    store's buy-alert matches pushed everything else off the panel;
 *  - the bespoke cost-change approval queue (approvers only), which predates
 *    the feed and stays its own actionable section rather than being folded
 *    into rows that only navigate.
 *
 * M34 swapped the legacy `rc-bell` styling for the V2 topbar composite: shared
 * IconButton / Button primitives, token-only surfaces, a popover panel on laptop
 * and a bottom sheet below 640px, Escape to dismiss and focus returned to the
 * bell. `IconComponent` is still honoured for the legacy topbars that pass their
 * own glyph; with nothing passed the shared Bell icon renders.
 */
const NotificationsBell = ({ IconComponent }) => {
  const history = useHistory();
  // `UserContext = createContext()` carries NO default, so this read is undefined
  // wherever the provider is not mounted, and destructuring it threw — taking down
  // any page whose layout includes this bell. No user ⇒ inert bell.
  const { user, accessibleStores, activeStoreId } = useContext(UserContext) ?? {};
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const refreshNotifications = useRefreshNotifications();

  // Owner of the ACTIVE store (what the API's mayReview checks), not the raw
  // home-store is_store_owner flag.
  const isApprover = isActiveStoreOwner(user, accessibleStores, activeStoreId);
  const { data } = usePendingChangeRequests(isApprover);
  const approvals = data?.items ?? [];

  const { data: summary } = useNotificationSummary(!!user);
  const unreadCount = summary?.unread_count ?? 0;
  const latest = useMemo(() => summary?.latest ?? [], [summary]);
  const groups = useMemo(() => groupNotifications(latest), [latest]);

  const count = approvals.length + unreadCount;

  // Focus moves into the panel on open so the keyboard lands where the content is;
  // Escape and an outside click both send it back to the bell.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => panelRef.current?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Click-outside and Escape dismiss, like every other floating panel in the shell.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus?.();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const goToRequest = (cr) => {
    setOpen(false);
    if (cr.order_id) history.push(`/request/order/${cr.order_id}`);
  };

  const openNotification = (item) => {
    setOpen(false);
    if (!item.isRead) {
      markNotificationRead(item.id).then(refreshNotifications).catch(() => {});
    }
    if (item.url) history.push(item.url);
  };

  const onMarkAllRead = () => {
    markAllNotificationsRead().then(refreshNotifications).catch(() => {});
  };

  const goToAll = () => {
    setOpen(false);
    history.push("/notifications");
  };

  const showApprovals = isApprover && approvals.length > 0;
  const isEmpty = !showApprovals && latest.length === 0;

  return (
    <div className="ft-notif" ref={wrapRef}>
      <IconButton
        ref={triggerRef}
        variant="ghost"
        label={count ? `Notifications (${count})` : "Notifications"}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="ft-notif__trigger"
        onClick={() => setOpen((v) => !v)}
      >
        {IconComponent ? <IconComponent /> : <Icon name="Bell" size="sm" />}
        {/* A dot, not a count — see the note in AppMobileTopbar. The number
            stays in the trigger's accessible name above. */}
        {count > 0 && <span className="ft-notif__dot" aria-hidden="true" />}
      </IconButton>

      {open && (
        <>
          {/* Sheet scrim — only painted below 640px, where the panel is a bottom sheet. */}
          <div className="ft-notif__scrim" aria-hidden="true" onMouseDown={() => setOpen(false)} />
          <div className="ft-notif__panel" role="dialog" aria-label="Notifications" ref={panelRef} tabIndex={-1}>
            <span className="ft-notif__handle" aria-hidden="true" />
            <div className="ft-notif__head">
              <p className="ft-notif__title">Notifications</p>
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={onMarkAllRead}>Mark all read</Button>
              )}
            </div>

            {isEmpty ? (
              <p className="ft-notif__empty">
                <span className="ft-notif__empty-icon" aria-hidden="true"><Icon name="Inbox" size="md" /></span>
                You’re all caught up.
              </p>
            ) : (
              <>
                {showApprovals && (
                  <>
                    <p className="ft-notif__group-label">
                      Needs approval
                      <span className="ft-notif__group-count">{approvals.length}</span>
                    </p>
                    <ul className="ft-notif__list">
                      {approvals.map((cr) => (
                        <li key={`cr-${cr.id}`}>
                          <button type="button" className="ft-notif__item" onClick={() => goToRequest(cr)}>
                            <span className="ft-notif__item-main">
                              Cost change · {cr.reference || `Listing ${cr.listing_id}`}
                            </span>
                            <span className="ft-notif__item-sub">
                              {cr.requested_by || "—"}
                              {" · "}
                              {Object.keys(cr.changes || {}).length} field
                              {Object.keys(cr.changes || {}).length === 1 ? "" : "s"}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* Master #569 owns what a feed row IS: grouped by kind, newest first
                    inside each group, rendered by the shared `NotificationItem` so the
                    product context and per-kind icon come with it. Fontaine owns the
                    chrome around them. `NotificationItem` still emits its own `rc-note`
                    markup — reconciling that row with `ft-notif` is a later milestone,
                    not this integration repair. */}
                {groups.map((group) => (
                  <React.Fragment key={group.key}>
                    <p className="ft-notif__group-label">
                      {group.label}
                      {group.unread > 0 && (
                        <span className="ft-notif__group-count">{group.unread}</span>
                      )}
                    </p>
                    {group.items.map((item) => (
                      <NotificationItem
                        key={item.id}
                        item={item}
                        onOpen={openNotification}
                        compact
                      />
                    ))}
                  </React.Fragment>
                ))}
              </>
            )}

            <div className="ft-notif__foot">
              <Button variant="ghost" size="sm" fullWidth onClick={goToAll}>View all notifications</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsBell;
